import React from "react";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Target, AlertTriangle, TrendingUp, TrendingDown, Minus, Info } from "lucide-react";

/**
 * Formats a percentage value for display
 */
const formatPercent = (value) => {
  if (value === null || value === undefined) return "-";
  return `${(value * 100).toFixed(1)}%`;
};

/**
 * Formats a currency value for display
 */
const formatCurrency = (value) => {
  if (value === null || value === undefined) return "-";
  return `$${Number(value).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

/**
 * Status badge color mapping
 */
const statusColors = {
  green: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
  yellow: "bg-amber-500/20 text-amber-300 border-amber-500/30",
  red: "bg-red-500/20 text-red-300 border-red-500/30",
  none: "bg-slate-500/20 text-slate-400 border-slate-500/30",
};

/**
 * Status label mapping
 */
const statusLabels = {
  green: "On Target",
  yellow: "Warning",
  red: "Critical",
  none: "No Target",
};

/**
 * BenchmarkStatusBadge - A small badge showing just the status
 */
export function BenchmarkStatusBadge({ status, lowConfidence = false, compact = false }) {
  if (!status || status === "none") return null;

  return (
    <Badge
      className={`text-xs border ${statusColors[status] || statusColors.none} ${compact ? "px-1.5 py-0" : ""}`}
      data-testid={`benchmark-status-${status}`}
    >
      {status === "green" && "✓"}
      {status === "yellow" && "⚠"}
      {status === "red" && "✗"}
      {lowConfidence && (
        <span className="ml-1 opacity-70" title="Low sample size">*</span>
      )}
    </Badge>
  );
}

/**
 * BenchmarkIndicator - Full benchmark display with target, variance, and status
 * Used in KPI cards and detail views
 */
export function BenchmarkIndicator({
  target,
  actual,
  variancePercent,
  status,
  scopeUsed,
  lowConfidence = false,
  format = "percent", // percent | currency | number | hours
  showTarget = true,
  showVariance = true,
  compact = false,
}) {
  if (!target && target !== 0) {
    return null;
  }

  // Format target value based on type
  const formattedTarget =
    format === "percent" ? formatPercent(target) :
    format === "currency" ? formatCurrency(target) :
    format === "hours" ? `${target}h` :
    target?.toLocaleString() || target;

  // Format variance
  const formattedVariance = variancePercent !== null && variancePercent !== undefined
    ? `${variancePercent >= 0 ? "+" : ""}${(variancePercent * 100).toFixed(1)}%`
    : null;

  // Determine variance direction icon
  const VarianceIcon = variancePercent > 0 ? TrendingUp : variancePercent < 0 ? TrendingDown : Minus;
  const varianceColor = 
    status === "green" ? "text-emerald-400" :
    status === "yellow" ? "text-amber-400" :
    status === "red" ? "text-red-400" :
    "text-slate-400";

  if (compact) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="inline-flex items-center gap-1">
              <BenchmarkStatusBadge status={status} lowConfidence={lowConfidence} compact />
            </div>
          </TooltipTrigger>
          <TooltipContent side="top" className="bg-slate-900 border-slate-700">
            <div className="space-y-1 text-xs">
              <div className="flex items-center gap-2">
                <Target className="h-3 w-3 text-slate-400" />
                <span className="text-slate-300">Target: {formattedTarget}</span>
              </div>
              {formattedVariance && (
                <div className="flex items-center gap-2">
                  <VarianceIcon className={`h-3 w-3 ${varianceColor}`} />
                  <span className={varianceColor}>Variance: {formattedVariance}</span>
                </div>
              )}
              {scopeUsed && (
                <div className="text-slate-500">Scope: {scopeUsed}</div>
              )}
              {lowConfidence && (
                <div className="flex items-center gap-1 text-amber-400">
                  <Info className="h-3 w-3" />
                  Low sample size
                </div>
              )}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <div className="flex items-center gap-2 text-xs" data-testid="benchmark-indicator">
      {/* Status Badge */}
      <Badge className={`border ${statusColors[status] || statusColors.none}`}>
        {statusLabels[status] || "No Target"}
      </Badge>

      {/* Target Value */}
      {showTarget && (
        <span className="text-slate-400 flex items-center gap-1">
          <Target className="h-3 w-3" />
          {formattedTarget}
        </span>
      )}

      {/* Variance */}
      {showVariance && formattedVariance && (
        <span className={`flex items-center gap-1 ${varianceColor}`}>
          <VarianceIcon className="h-3 w-3" />
          {formattedVariance}
        </span>
      )}

      {/* Low Confidence Warning */}
      {lowConfidence && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="text-amber-400">
                <AlertTriangle className="h-3 w-3" />
              </span>
            </TooltipTrigger>
            <TooltipContent side="top" className="bg-slate-900 border-slate-700">
              <span className="text-xs">Low sample size - results may not be representative</span>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
}

/**
 * BenchmarkVarianceCell - For use in table cells
 * Shows variance with color coding
 */
export function BenchmarkVarianceCell({ 
  variancePercent, 
  status, 
  lowConfidence = false,
  showPercent = true 
}) {
  if (variancePercent === null || variancePercent === undefined) {
    return <span className="text-slate-500">-</span>;
  }

  const formattedVariance = showPercent 
    ? `${variancePercent >= 0 ? "+" : ""}${(variancePercent * 100).toFixed(1)}%`
    : null;

  const colorClass = 
    status === "green" ? "text-emerald-400" :
    status === "yellow" ? "text-amber-400" :
    status === "red" ? "text-red-400" :
    "text-slate-400";

  const VarianceIcon = variancePercent > 0 ? TrendingUp : variancePercent < 0 ? TrendingDown : Minus;

  return (
    <div className={`flex items-center gap-1 ${colorClass}`}>
      <VarianceIcon className="h-3 w-3" />
      {showPercent && <span>{formattedVariance}</span>}
      {lowConfidence && <span className="opacity-60">*</span>}
    </div>
  );
}

/**
 * StatusFilterSelect - For filtering tables by benchmark status
 */
export function StatusFilterSelect({ value, onChange, includeAll = true }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="bg-white/5 border border-white/10 rounded-md px-2 py-1 text-sm text-slate-300"
      data-testid="status-filter-select"
    >
      {includeAll && <option value="all">All Status</option>}
      <option value="red">Critical Only</option>
      <option value="yellow">Warning & Critical</option>
      <option value="green">On Target</option>
    </select>
  );
}

/**
 * Hook to fetch benchmark evaluations
 */
export function useBenchmarkEvaluations() {
  const [benchmarks, setBenchmarks] = React.useState({});
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  const fetchBenchmarks = React.useCallback(async (api, startDate, endDate) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ format: "structured" });
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);

      const response = await api.get(`/analytics/benchmarks/evaluate?${params}`);
      
      // Convert array to map keyed by KPI name
      const benchmarkMap = {};
      for (const kpi of response.data?.kpis || []) {
        benchmarkMap[kpi.kpi] = kpi;
      }
      setBenchmarks(benchmarkMap);
      return benchmarkMap;
    } catch (e) {
      setError(e);
      console.error("Failed to fetch benchmarks:", e);
      return {};
    } finally {
      setLoading(false);
    }
  }, []);

  return { benchmarks, loading, error, fetchBenchmarks };
}

export default BenchmarkIndicator;
